import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { endOfDay, format, isBefore, isToday, startOfDay } from "date-fns";
import { Control, useWatch } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  InvoicesKeys,
  patchInvoiceHeader,
} from "@/features/invoices/api/invoices";
import { cn } from "@/lib/utils";

import { InvoiceFormValues } from "./types";

interface DueDatePickerProps {
  control: Control<InvoiceFormValues>;
  disabled?: boolean;
  invoiceId?: string | null;
}

const DueDatePicker = ({
  control,
  disabled,
  invoiceId,
}: DueDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { mutate: updateDueDate } = useMutation({
    mutationFn: (dueDate: number) =>
      patchInvoiceHeader(invoiceId!, { dueDate }),
    onSuccess: () => {
      // Invalidate invoices query after updating due date
      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });
    },
  });

  // Watch for due date changes
  const dueDate = useWatch({
    control,
    name: "dueDate",
  });

  // Handle due date change
  const handleDueDateChange = (date: Date) => {
    if (!invoiceId) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      let dueDateValue = date;
      if (isToday(dueDateValue)) {
        dueDateValue = endOfDay(dueDateValue);
      }
      // Convert to unix timestamp in seconds
      const unixTimestamp = Math.floor(dueDateValue.getTime() / 1000);
      updateDueDate(unixTimestamp);
    }, 300);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <FormField
      control={control}
      name="dueDate"
      render={({ field }) => (
        <FormItem className="flex flex-col space-y-2">
          <FormLabel>Due Date</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full pl-3 text-left font-normal",
                    !field.value && "text-muted-foreground"
                  )}
                  disabled={disabled}
                >
                  {field.value ? (
                    format(new Date(field.value), "MMMM d, yyyy")
                  ) : (
                    <span>Select a date</span>
                  )}
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => {
                  if (date) {
                    field.onChange(date);
                    handleDueDateChange(date);
                    setOpen(false);
                  }
                }}
                disabled={(date) => {
                  const today = startOfDay(new Date());
                  return isBefore(startOfDay(date), today) || disabled === true;
                }}
                initialFocus
                className="pointer-events-auto"
                classNames={{
                  day_today: "bg-gray-200 text-gray-900",
                }}
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default DueDatePicker;
